import Select from '../atoms/inputs/select';
import { Text } from '../atoms/text';

/**
 * One simple option with a numeric identifier and display label.
 */
export interface NamedOption {
  /** Stable option identifier used as the select value. */
  id: number;
  /** Human-readable option label. */
  name: string;
}

/**
 * Props for the named single-select control.
 */
interface NamedOptionSingleSelectProps {
  /** Field title shown above the select. */
  title: string;
  /** Options available for selection. */
  options: NamedOption[];
  /** Whether the control is currently loading options. */
  loading: boolean;
  /** Selected option id or null when empty. */
  value: number | null;
  /** Placeholder shown before any option is selected. */
  placeholder: string;
  /** Called whenever the selected option changes. */
  onChange: (optionId: number | null) => void;
}

/**
 * Shared single-select control for numeric options that expose a name label.
 */
export const NamedOptionSingleSelect: React.FC<
  NamedOptionSingleSelectProps
> = ({ title, options, loading, value, placeholder, onChange }) => (
  <label className="block space-y-1">
    <Text
      as="span"
      size="tiny"
      weight="medium"
      className="uppercase tracking-wide text-neutral-500"
    >
      {title}
    </Text>
    <Select
      loading={loading}
      variant="neutral"
      value={value ?? ''}
      onChange={(event) =>
        onChange(event.target.value ? Number(event.target.value) : null)
      }
      options={[
        {
          content: placeholder,
          value: '',
        },
        ...options.map((option) => ({
          content: option.name,
          value: String(option.id),
        })),
      ]}
    />
  </label>
);
